/**
 * Token Storage Utility
 * 
 * Manages JWT tokens in sessionStorage for secure authentication.
 * Uses sessionStorage instead of localStorage for better security -
 * tokens are cleared when the browser tab is closed.
 */

export interface TokenStorage {
  accessToken: string;
  idToken: string;
  refreshToken: string;
  expiresAt: number; // Unix timestamp in milliseconds
}

const TOKEN_STORAGE_KEY = 'auth_tokens';

/**
 * Save JWT tokens to sessionStorage
 * @param tokens - The tokens to store
 */
export function saveTokens(tokens: TokenStorage): void {
  try {
    const tokenData = JSON.stringify(tokens);
    sessionStorage.setItem(TOKEN_STORAGE_KEY, tokenData);
  } catch (error) {
    console.error('Failed to save tokens:', error);
    throw new Error('Failed to save authentication tokens');
  }
}

/**
 * Retrieve JWT tokens from sessionStorage
 * @returns The stored tokens or null if not found
 */
export function getTokens(): TokenStorage | null {
  try {
    const tokenData = sessionStorage.getItem(TOKEN_STORAGE_KEY);
    if (!tokenData) {
      return null;
    }
    return JSON.parse(tokenData) as TokenStorage;
  } catch (error) {
    console.error('Failed to retrieve tokens:', error);
    return null;
  }
}

/**
 * Clear all tokens from sessionStorage (used during logout)
 */
export function clearTokens(): void {
  try {
    sessionStorage.removeItem(TOKEN_STORAGE_KEY);
  } catch (error) {
    console.error('Failed to clear tokens:', error);
  }
}

/**
 * Check if a token is expired based on the expiresAt timestamp
 * @param expiresAt - Unix timestamp in milliseconds
 * @returns true if the token is expired, false otherwise
 */
export function isTokenExpired(expiresAt: number): boolean {
  const now = Date.now();
  // Add a 60-second buffer to refresh tokens before they actually expire
  const bufferMs = 60 * 1000;
  return now >= (expiresAt - bufferMs);
}

/**
 * Get the access token if it exists and is not expired
 * @returns The access token or null if not found or expired
 */
export function getAccessToken(): string | null {
  const tokens = getTokens();
  if (!tokens) {
    return null;
  }

  if (isTokenExpired(tokens.expiresAt)) {
    return null;
  }

  return tokens.accessToken;
}

/**
 * Get the ID token if it exists and is not expired
 * @returns The ID token or null if not found or expired
 */
export function getIdToken(): string | null {
  const tokens = getTokens();
  if (!tokens) {
    return null;
  }

  if (isTokenExpired(tokens.expiresAt)) {
    return null;
  }

  return tokens.idToken;
}
